import { z } from "zod";

/**
 * Design a blocchi dei template email (editor drag & drop).
 *
 * Il design (JSON) è la "sorgente" modificabile nell'editor; l'HTML
 * email-safe viene compilato da `renderEmailDesign` e salvato in
 * `email_templates.body_html`, che resta il formato usato dagli invii.
 */

// ── palette / costanti ──────────────────────────────────────────────
export const EMAIL_FONT_STACKS = {
  sans: "Arial, Helvetica, sans-serif",
  modern: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  mono: "'Courier New', Courier, monospace",
} as const;
export type EmailFontKey = keyof typeof EMAIL_FONT_STACKS;

export const SOCIAL_NETWORKS = [
  "website",
  "facebook",
  "instagram",
  "linkedin",
  "x",
  "youtube",
  "tiktok",
] as const;
export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];

const hexColor = z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
const align = z.enum(["left", "center", "right"]).default("left");
const url = z.string().trim().max(2000);

// ── blocchi ─────────────────────────────────────────────────────────
const base = { id: z.string().min(1).max(40) };

export const headingBlockSchema = z.object({
  ...base,
  type: z.literal("heading"),
  text: z.string().max(500).default(""),
  level: z.enum(["h1", "h2", "h3"]).default("h1"),
  align,
  color: hexColor.optional(),
});

export const textBlockSchema = z.object({
  ...base,
  type: z.literal("text"),
  /** HTML inline semplice (b/i/u/a/br); viene ripulito in compilazione. */
  html: z.string().max(20000).default(""),
  align,
  fontSize: z.number().int().min(10).max(28).default(15),
  color: hexColor.optional(),
});

export const imageBlockSchema = z.object({
  ...base,
  type: z.literal("image"),
  src: url.default(""),
  alt: z.string().max(300).default(""),
  href: url.optional().nullable(),
  /** larghezza in % dell'area contenuto */
  widthPercent: z.number().int().min(10).max(100).default(100),
  align: z.enum(["left", "center", "right"]).default("center"),
  radius: z.number().int().min(0).max(32).default(0),
});

export const buttonBlockSchema = z.object({
  ...base,
  type: z.literal("button"),
  text: z.string().max(200).default(""),
  href: url.default(""),
  align: z.enum(["left", "center", "right"]).default("center"),
  bgColor: hexColor.optional(),
  textColor: hexColor.optional(),
  radius: z.number().int().min(0).max(32).default(8),
});

export const dividerBlockSchema = z.object({
  ...base,
  type: z.literal("divider"),
  color: hexColor.optional(),
  paddingY: z.number().int().min(0).max(64).default(12),
});

export const spacerBlockSchema = z.object({
  ...base,
  type: z.literal("spacer"),
  height: z.number().int().min(4).max(160).default(24),
});

export const socialBlockSchema = z.object({
  ...base,
  type: z.literal("social"),
  align: z.enum(["left", "center", "right"]).default("center"),
  links: z
    .array(z.object({ network: z.enum(SOCIAL_NETWORKS), url }))
    .max(7)
    .default([]),
});

export const footerBlockSchema = z.object({
  ...base,
  type: z.literal("footer"),
  html: z.string().max(5000).default(""),
  align: z.enum(["left", "center", "right"]).default("center"),
});

/** Blocchi "semplici" ammessi dentro le colonne (niente nesting ulteriore). */
export const simpleBlockSchema = z.discriminatedUnion("type", [
  headingBlockSchema,
  textBlockSchema,
  imageBlockSchema,
  buttonBlockSchema,
  dividerBlockSchema,
  spacerBlockSchema,
]);
export type SimpleBlock = z.infer<typeof simpleBlockSchema>;

export const columnsBlockSchema = z.object({
  ...base,
  type: z.literal("columns"),
  columns: z
    .array(z.object({ blocks: z.array(simpleBlockSchema).max(20) }))
    .min(2)
    .max(3),
});

export const emailBlockSchema = z.discriminatedUnion("type", [
  headingBlockSchema,
  textBlockSchema,
  imageBlockSchema,
  buttonBlockSchema,
  dividerBlockSchema,
  spacerBlockSchema,
  socialBlockSchema,
  footerBlockSchema,
  columnsBlockSchema,
]);
export type EmailBlock = z.infer<typeof emailBlockSchema>;
export type EmailBlockType = EmailBlock["type"];

// ── design ──────────────────────────────────────────────────────────
export const emailDesignSettingsSchema = z.object({
  backgroundColor: hexColor.default("#f1f5f9"),
  contentBackgroundColor: hexColor.default("#ffffff"),
  contentWidth: z.number().int().min(480).max(720).default(600),
  font: z.enum(["sans", "modern", "serif", "mono"]).default("modern"),
  textColor: hexColor.default("#1e293b"),
  brandColor: hexColor.default("#4f46e5"),
});
export type EmailDesignSettings = z.infer<typeof emailDesignSettingsSchema>;

export const emailDesignSchema = z.object({
  version: z.literal(1),
  settings: emailDesignSettingsSchema,
  blocks: z.array(emailBlockSchema).max(80),
});
export type EmailDesign = z.infer<typeof emailDesignSchema>;

export function createEmptyEmailDesign(): EmailDesign {
  return emailDesignSchema.parse({ version: 1, settings: {}, blocks: [] });
}

// ── sanitizzazione minima dell'HTML inline ──────────────────────────
/**
 * Difesa in profondità sui contenuti dei blocchi testo/footer:
 * rimuove script/style/iframe, gli handler on* e gli URL javascript:.
 * (L'HTML dei template è comunque scritto dal tenant stesso.)
 */
export function sanitizeInlineHtml(input: string): string {
  return input
    .replace(/<\s*(script|style|iframe|object|embed)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, "")
    .replace(/<\s*(script|style|iframe|object|embed)[^>]*\/?\s*>/gi, "")
    .replace(/\son\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/(href|src)\s*=\s*(["']?)\s*javascript:[^"'>\s]*\2/gi, '$1="#"');
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// ── compilazione design → HTML email-safe ───────────────────────────
const HEADING_SIZES: Record<"h1" | "h2" | "h3", { size: number; height: number }> = {
  h1: { size: 28, height: 36 },
  h2: { size: 22, height: 30 },
  h3: { size: 18, height: 26 },
};

const SOCIAL_LABELS: Record<SocialNetwork, string> = {
  website: "Sito web",
  facebook: "Facebook",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  x: "X",
  youtube: "YouTube",
  tiktok: "TikTok",
};

interface RenderCtx {
  settings: EmailDesignSettings;
  font: string;
  /** larghezza utile (contentWidth meno il padding orizzontale) */
  innerWidth: number;
}

const PAD_X = 28;

function renderSimpleBlock(block: SimpleBlock, ctx: RenderCtx, colWidth?: number): string {
  const { settings, font } = ctx;
  const width = colWidth ?? ctx.innerWidth;

  switch (block.type) {
    case "heading": {
      const { size, height } = HEADING_SIZES[block.level];
      const color = block.color ?? settings.textColor;
      return `<div style="font-family:${font};font-size:${size}px;line-height:${height}px;font-weight:bold;color:${color};text-align:${block.align};padding:8px 0">${escapeHtml(block.text)}</div>`;
    }
    case "text": {
      const color = block.color ?? settings.textColor;
      return `<div style="font-family:${font};font-size:${block.fontSize}px;line-height:1.6;color:${color};text-align:${block.align};padding:6px 0">${sanitizeInlineHtml(block.html)}</div>`;
    }
    case "image": {
      if (!block.src) return "";
      const imgWidth = Math.round((width * block.widthPercent) / 100);
      const radius = block.radius > 0 ? `border-radius:${block.radius}px;` : "";
      const img = `<img src="${escapeHtml(block.src)}" alt="${escapeHtml(block.alt)}" width="${imgWidth}" style="display:block;width:${imgWidth}px;max-width:100%;height:auto;${radius}border:0" />`;
      const linked = block.href
        ? `<a href="${escapeHtml(block.href)}" target="_blank">${img}</a>`
        : img;
      return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="${block.align}" style="margin:${block.align === "center" ? "0 auto" : "0"}"><tr><td style="padding:8px 0">${linked}</td></tr></table>`;
    }
    case "button": {
      const bg = block.bgColor ?? settings.brandColor;
      const fg = block.textColor ?? "#ffffff";
      return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="${block.align}" style="margin:${block.align === "center" ? "0 auto" : "0"}"><tr><td style="padding:10px 0"><a href="${escapeHtml(block.href || "#")}" target="_blank" style="display:inline-block;background-color:${bg};color:${fg};font-family:${font};font-size:15px;font-weight:600;text-decoration:none;padding:13px 30px;border-radius:${block.radius}px">${escapeHtml(block.text)}</a></td></tr></table>`;
    }
    case "divider": {
      const color = block.color ?? "#e2e8f0";
      return `<div style="padding:${block.paddingY}px 0"><div style="border-top:1px solid ${color};line-height:1px;font-size:1px">&nbsp;</div></div>`;
    }
    case "spacer":
      return `<div style="height:${block.height}px;line-height:${block.height}px;font-size:1px">&nbsp;</div>`;
  }
}

function renderBlock(block: EmailBlock, ctx: RenderCtx): string {
  const { settings, font } = ctx;

  switch (block.type) {
    case "social": {
      if (block.links.length === 0) return "";
      const links = block.links
        .map(
          (l) =>
            `<a href="${escapeHtml(l.url)}" target="_blank" style="color:${settings.brandColor};font-family:${font};font-size:13px;font-weight:600;text-decoration:none">${SOCIAL_LABELS[l.network]}</a>`,
        )
        .join(`<span style="color:#cbd5e1">&nbsp;&nbsp;·&nbsp;&nbsp;</span>`);
      return `<div style="text-align:${block.align};padding:10px 0">${links}</div>`;
    }
    case "footer":
      return `<div style="font-family:${font};font-size:12px;line-height:1.6;color:#94a3b8;text-align:${block.align};padding:10px 0">${sanitizeInlineHtml(block.html)}</div>`;
    case "columns": {
      const n = block.columns.length;
      const gap = 16;
      const colWidth = Math.floor((ctx.innerWidth - gap * (n - 1)) / n);
      const tds = block.columns
        .map((col, i) => {
          const inner = col.blocks.map((b) => renderSimpleBlock(b, ctx, colWidth)).join("");
          const padLeft = i === 0 ? 0 : gap;
          return `<td class="kairos-stack" width="${colWidth}" valign="top" style="width:${colWidth}px;padding-left:${padLeft}px;vertical-align:top">${inner}</td>`;
        })
        .join("");
      return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%"><tr>${tds}</tr></table>`;
    }
    default:
      return renderSimpleBlock(block, ctx);
  }
}

/** Compila il design in un documento HTML compatibile con i client email. */
export function renderEmailDesign(design: EmailDesign): string {
  const settings = design.settings;
  const font = EMAIL_FONT_STACKS[settings.font];
  const ctx: RenderCtx = { settings, font, innerWidth: settings.contentWidth - PAD_X * 2 };

  const body = design.blocks.map((b) => renderBlock(b, ctx)).join("\n");

  return `<!doctype html>
<html lang="it">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<style>
  @media only screen and (max-width: 620px) {
    .kairos-container { width: 100% !important; }
    .kairos-stack { display: block !important; width: 100% !important; padding-left: 0 !important; }
  }
</style>
</head>
<body style="margin:0;padding:0;background-color:${settings.backgroundColor}">
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;background-color:${settings.backgroundColor}">
<tr><td align="center" style="padding:24px 12px">
<table role="presentation" class="kairos-container" cellpadding="0" cellspacing="0" border="0" width="${settings.contentWidth}" style="width:${settings.contentWidth}px;max-width:100%;background-color:${settings.contentBackgroundColor};border-radius:12px">
<tr><td style="padding:32px ${PAD_X}px">
${body}
</td></tr>
</table>
</td></tr>
</table>
</body>
</html>`;
}
